import React from 'react';
import { useTranslation } from 'react-i18next';
import Helmet from 'react-helmet';

import { Button, Container, FormControl, FormControlLabel, Grid, makeStyles, Radio, RadioGroup, TextField, Typography, useTheme, useMediaQuery, Theme, MenuItem, Select, CircularProgress, Checkbox } from '@material-ui/core';

import ScrollToTop from '../../../../common/ScrollToTop';
import Cost from './../common/Cost';
import Map from './Map';
import SelectAirport from './Form/SelectAirport';
import { AirportTransferTaxiMapMode, UNSELECTED_RIDING_DATE_STRING, useForm } from './Form/useForm';
import PickupPattern from './Form/PickupPattern';
import SelectFlightHour from './Form/SelectFlightHour';
import SelectFlightMinutes from './Form/SelectFlightMinutes';
import SelectHour from './Form/SelectHour';
import SelectMinutes from './Form/SelectMinutes';
import SelectDay from './Form/SelectDay';
import SelectMonth from './Form/SelectMonth';
import SelectYear from './Form/SelectYear';
import { ButtonToSubmit } from './Form/ButtonToSubmit';
import Cautions from '../common/Cautions';
import LoadingModal from '../../../../search/LoadingModal';
import Contact from '../common/Contact';
import AlertInReservation from '../../MiyakoTaxi/AlertInReservation';
import { Alert } from '@material-ui/lab';

import ShowTaxiComapnyLogo from './ShowTaxiComapnyLogo';
import { TaxiCompanyList } from '../../../../../services/taxi/GetDirectionAndFare/models/getFareSamplesParams';
import { useDispatch, useSelector } from 'react-redux';
import { State } from '../../../../../reducers';
import { TaxiTimePattern } from '../../../../../services/taxi/GetTaxiMetaInfo/model/taxiCompanyConf';
import SelectHourAndMinutesPulldown from './Form/SelectHourAndMinutesPulldown';
import AirportTransferTaxiFooter from '../common/Footer';
import AirportTransferTaxiHeader from '../common/Header';
import { isTabiCube } from '../common/TabiCube';
import { switchUseV2 } from '../../../../../actions/generalAction';
import Environment from '../../../../../utils/environment';
import SelectFlight from './Form/SelectFlight';
import { DateTime, Interval } from 'luxon';
import { HelmetProvider } from 'react-helmet-async';
import AdobeAnalyticsScript from '../../../Common/AdobeAnalyticsScript';

const useStyles = makeStyles((theme) => ({
  contents: {
    padding: '5px',
    border: 'solid 1px #000000',
    height: 'calc(100% - 12px)'
  },
  alert: {
    margin: '10px 0px',
  },
  imgContainer: {
    alignItems: 'flex-start',
    marginTop: '10px',
    minHeight: 'min(18vw, 200px)'
  },
  // 補足の部分のスタイル設定。
  supplement: {
    fontSize: '0.875rem',
  },
  tableLeft: {
    backgroundColor: theme.palette.primary.main,
    border: 'solid 1px #000000',
  },
  textItemName: {
    color: '#ffffff',
    fontSize: '0.8rem',
    fontWeight: 'bold',
    padding: '5px',
  },
  textItemAnnotation: {
    color: '#ffff00',
    fontSize: '0.8rem',
    fontWeight: 'bold'
  },
  seaGaiaSelect: {
    height: '2.2rem',
    width: '90%',
    fontSize: '0.75rem'
  },
  /** 【デバッグ専用】dummyVal限定の地図⇔プルダウン切替ボタン領域 */
  debugModeToggle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    margin: '8px 0',
    padding: '8px 12px',
    border: '2px dashed #e65100',
    borderRadius: '4px',
    backgroundColor: '#fff3e0',
  },
  debugModeLabel: {
    color: '#e65100',
    fontWeight: 'bold',
    fontSize: '0.75rem',
  },
  debugModeToggleButton: {
    color: '#e65100',
    borderColor: '#e65100',
    fontWeight: 'bold',
    whiteSpace: 'nowrap',
  },
  taxiUnderLogoNoticeArea: {
    fontSize: '0.875rem',
    color: '#0B308E'
  },
  redZone: {
    color: '#f44336'
  },
  flightBefore: {
    paddingRight: "10px"
  },
  springMileMain: {
    color: '#FF0000',
    paddingTop: '1rem',
    paddingBottom: '1rem',
    fontSize: '0.875rem'
  },
  springMileP: {
    margin: '0rem'
  },
  springMilePBold: {
    margin: '0rem',
    fontWeight: 'bold'
  },
  // 庄内タクシー期間限定キャンペーンエリア
  shounaiCampaignArea: {
    marginTop: '1rem'
  }
}));

/** タクシー・ハイヤー予約画面のコンポーネント。 */
export const Booking: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const classes = useStyles(theme);
  /** 実行環境を判別するための環境変数。 */
  const env = process.env['REACT_APP_STAGE_ENV'] || '';
  /** reCAPTCHA v2を使用するかを表すフラグ。 */
  const useRecaptchaV2 = useSelector((state: State) => state.general.useV2);
  const errorMessage = useSelector((state: State) => state.general.errorMessage);

  const isMobileSize = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('sm')
  );

  // タクシー予約画面で設定された情報に関するカスタムフック。
  const {
    airportCode,  onChangedAirport,
    center, setCenter,
    markerPoint, setMarkerPoint,
    pointName, setPointName,
    setPlaceJP,
    email, onChangedEmail, isEmptyEmail, isInvalidEmail,
    isUnavalableEmail, isRequiredRetry, isFailed,
    flight, onChangedFlight,
    isEmptyFlight, isInvalidFlight,
    flightDate,  onChangedFlightDate,
    ridingDate,  onChangedRidingYear, onChangedRidingMonth,onChangedRidingDay,
    onChangedRidingHour, onChangedRidingMinutes, onChangedRidingHourAndMinutes,
    isOverDeadline,
    toAirport,  pickupPattern, setPickupPattern,
    isSubmitted,
    onSubmit,
    setMunicipality,
    isSubjectArea,
    distanceText, durationText,
    switchEdited,
    taxiCompany,
    setPlace,
    mode, toggleDebugPulldownMode,
    selectPointNumber, setSelectPointNumber,
    pointList,
    invalidFlightErrorMessage,
    feeLabel,
    isEndCallTaxiAPI,
    isFailCallTaxiAPI,
    targetAirportCodeList,
    isEndInit,
    fareType,
    flightPattern,
    selectFlightData,
    onChangedRidingFlight,
    notesLabel, alertLabel,
    isAvailableReservation,
    isEmptyAddress,
    taxiCompanyConf,
    lang,
    timePattern,
    selectTimeList,
    isUnselectedRidingDate,
    referrer,
    needRetryRecaptcha,
    switchNeedRetryRecaptcha,
    disallowSupportDirection,
    invalidFlightPattern,
    isUnselectedFlightHour,
    isUnselectedFlightMinutes,
    invalidFlightDateErrorMessage,
    isUnselectedFlightDate,
    debugReservationDatetime, setDebugReservationDatetime,
  } = useForm();

  /** コスト（所要時間・距離）のラベルを取得する。 */
  const costLabel = () => {
    return `${distanceText} ${durationText}`;
  }

  /** メールアドレス欄に不備があるかを表すフラグ。 */
  // 予約ボタン押下済みで、メールアドレスが空または文字列がメールアドレスの形式になっていない場合はtrueとする。
  const hasErrorInEmail = isSubmitted && (isEmptyEmail || isInvalidEmail);

  /** 搭乗便欄に不備があるかを表すフラグ。 */
  // 予約ボタン押下済みで、メールアドレスが空または文字列が搭乗便の形式になっていない場合はtrueとする。
  const hasErrorInFlight = isSubmitted && ((isEmptyFlight && selectFlightData === UNSELECTED_RIDING_DATE_STRING) || isInvalidFlight);

  /** エラー時にメールアドレス欄で表示するメッセージを取得する。 */
  const getEmailHelperLabel = () => {
    let label = '';
    if (isEmptyEmail) { label = t('taxi:empty_email'); }
    if (isInvalidEmail) { label = t('taxi:invalid_email') }
    return label;
  }

  /** エラー時に搭乗便欄で表示するメッセージを取得する。 */
  const getFlightHelperLabel = () => {
    let label = '';
    if (isEmptyFlight && selectFlightData === UNSELECTED_RIDING_DATE_STRING) { label = t('taxi:empty_fligtnumber'); }
    if (isInvalidFlight) { label = t('taxi:invalid_flightnumber') }
    return label;
  }

  /** タクシー料金・距離・時間を表示するか判定する */
  const isShowTaxiFeeArea = () => {
    return isEndCallTaxiAPI && !isFailCallTaxiAPI;
  }

  /** 旅CUBEモードであるかを表すフラグ。 */
  const tabiCube = isTabiCube(referrer || '');
  /** ページタイトル。 */
  const title = tabiCube ? t('tabicube') : t('title');

  /** 予約ボタンクリック時の処理。 */
  const handleClick = () => {
    // reCaptcha v2での認証を促すフラグを一旦オフにする。
    switchNeedRetryRecaptcha(false);
    // reCAPTCHA v2を使用する場合。
    if (useRecaptchaV2) {
      /** reCAPTCHA v2用のエレメント。 */
      const wrapper = document.createElement('div');
      const widgetId = grecaptcha.render(wrapper, {
        sitekey: process.env.REACT_APP_GOOGLE_RECAPTCHA_V2_KEY,
        size: 'invisible',
        callback: (token: string) => {
          // 予約処理の関数。
          onSubmit(token);
        }
      });
      // reCAPTCHA v2用のエレメントをbodyタグ内に追加する。
      document.body.appendChild(wrapper);
      // reCAPCHA v2のチャレンジを実行する。
      grecaptcha.execute(widgetId);
    } else {
      // reCAPTCHA v3を使用する場合、そのまま仮予約処理を実行する。
      onSubmit();
    }
  };

  /** Google reCAPTCHAのjsファイルのURL。 */
  const recaptchaUrl = `https://www.google.com/recaptcha/api.js?render=${process.env.REACT_APP_GOOGLE_RECAPTCHA_KEY}`;

  /** 庄内エリアのタクシー会社か？ */
  const isShonaiAreaTaxi = [TaxiCompanyList.sakataNo1Taxi, TaxiCompanyList.shokoHire].includes(taxiCompany);

  /** 便接続の乗合タクシーか？ */
  const isFlightConnectionSharedTaxi = [TaxiCompanyList.sakataNo1Taxi, TaxiCompanyList.shokoHire, TaxiCompanyList.iwakiTaxi].includes(taxiCompany);

  /** 酒田第一タクシーベースのタクシーか？ */
  const isSakataBaseformTaxi = [TaxiCompanyList.sakataNo1Taxi, TaxiCompanyList.iwakiTaxi].includes(taxiCompany);

  /** 象潟合同交通か？ */
  const isKisakataTaxi = [TaxiCompanyList.airport_liner, TaxiCompanyList.nikaho_go].includes(taxiCompany);

  /** 象潟合同交通か？ */
  const isInariTaxi = [TaxiCompanyList.inari, TaxiCompanyList.inarihw].includes(taxiCompany);

  /** 庄交ハイヤー・酒田第一タクシーかつ、campaign期間内か？ */
  const isShokouSakataWithCampaign = (()=>{
    /** 庄交ハイヤー・酒田第一タクシーでない場合には出さない */
    if(!isShonaiAreaTaxi) {
      return false;
    }
    /** 期間内に収まっているか判定 */
    const now = DateTime.now();
    const end = DateTime.fromISO("2025-03-01T00:00:00+09:00");
    return now < end;
  })();

  /** 支払いに関する注意文言 */
  const paymentLabel: string = (() => {
    switch (taxiCompany) {
      case TaxiCompanyList.sakataNo1Taxi:
      case TaxiCompanyList.shokoHire:
      case TaxiCompanyList.iwakiTaxi:
      case TaxiCompanyList.airport_liner:
      case TaxiCompanyList.nikaho_go:
      case TaxiCompanyList.inari:
      case TaxiCompanyList.inarihw:
        return '';
      case TaxiCompanyList.iyotetsutaxi:
      case TaxiCompanyList.nahahire:
      case TaxiCompanyList.tsubamelimo:
        return "※ハイヤー料金は現地でのお支払いとなります。";
      default:
        return "※タクシー料金は現地でのお支払いとなります。";
    }
  })();

  return (
    <div>
      <HelmetProvider>
        <Helmet>
          {/* ページタイトルを設定する。 */}
          <title>{title}</title>
          {/* Google reCAPTHCAのJavaScriptファイルの読み込み。 */}
          <script src={recaptchaUrl} async defer />
          {/* Adobe Analyticsでタクシー予約画面のアクセス数の集計をする。 */}
          {/* <script type="application/javascript">
            s.pageName=location.origin+&quot;/booking-taxi&quot;;
            s.t();
          </script> */}
        </Helmet>
      </HelmetProvider>
        <>
          <AdobeAnalyticsScript path="/booking-taxi"/>
          {/* 画面遷移時に画面トップへ遷移させるためのコンポーネント。 */}
          <ScrollToTop />
          {/* ヘッダー。 */}
          <AirportTransferTaxiHeader tabiCube={tabiCube} />
          {/* 仮予約の処理中に表示するアイコン。 */}
          <LoadingModal />
          {/** 読込中に表示するアイコン */}
          {!isEndInit &&
            <Container maxWidth="md">
              <Grid container direction="column">
                <Grid item container justify='center' style={{ margin: '20px' }}>
                  <CircularProgress color='primary' size={64}/>
                </Grid>
              </Grid>
            </Container>
          }
          {/* 読み込みが終わった際にBodyを表示する */}
          {isEndInit &&
            <Container maxWidth="md">
              <Grid container direction="column">
                <Grid item className={classes.imgContainer}>
                  {/* 企業ロゴを表示する */}
                  <ShowTaxiComapnyLogo
                    company={taxiCompany}
                    lang={lang}
                  />
                  {/** イースタンの場合にはここ表示しない */}
                  {/** 多言語化対応はイースタンのみのため対応をしない（2022/02/18 現在） */}
                  { (taxiCompany !== TaxiCompanyList.eastern && taxiCompany !== TaxiCompanyList.limoTaxi) &&
                    <div className={classes.taxiUnderLogoNoticeArea}>
                      <div>
                        {taxiCompanyConf?.displayName}のご予約が可能です。内容をご確認いただき、よろしければ、「予約に進む」ボタンを選択してください。本ページでご入力いただいたメールアドレスに確認メールが送信されます。
                      </div>
                      <div className={classes.redZone}>
                        {/* キングタクシー2のみ */}
                        {(taxiCompany === TaxiCompanyList.kingTaxi2) &&
                          <>
                            ※ご注意<br/>
                            こちらの予約はエアポートライナー（乗合タクシー）「速籠便（秋田市内）」のものではありません。<br/>
                          </>
                        }
                        {/* 庄内エリアのタクシーか否かで文言が変わる */}
                        {(isFlightConnectionSharedTaxi || isKisakataTaxi) &&
                          <>
                            ※料金は現地でのお支払いとなります。
                          </>
                        }
                        {/* 稲荷タクシー */}
                        {(isInariTaxi) &&
                          <>
                            ※タクシー料金は現地でのお支払いとなります。
                          </>
                        }
                        {/* 「※ハイヤー or タクシー料金は現地でのお支払いとなります。」ラベル */}
                        {paymentLabel !== '' && <>
                          {paymentLabel}<br/>
                        </>}
                        {/* 北鹿観光ハイヤー: 迎車料金注記 */}
                        {(taxiCompany === TaxiCompanyList.hokuroku) &&
                          <>
                            *空港までの送りの場合に限り、迎車料金を含み目安料金をご案内しております。<br/>
                          </>
                        }
                        {/* 通津タクシー */}
                        {(taxiCompany === TaxiCompanyList.tsuzutaxi) &&
                          <>
                            ※ご予約は、ご乗車7日前から2日前の17時までとなります。
                          </>
                        }
                        {taxiCompany === TaxiCompanyList.miyakou &&
                          <>
                            ※運賃改定に伴い、2026年2月1日ご乗車分より特定エリア間の定額運賃を終了いたします。今後は全ての区間においてメーター料金での運行となります。
                            <br/>
                            ※表示される料金は、メーター目安料金でのご案内となります。
                          </>
                        }
                      </div>
                      {/* キャンペーン期間中のお知らせ掲載 */}
                      {isShokouSakataWithCampaign &&
                        <div className={classes.shounaiCampaignArea}>
                          <div>2025年1月9日(木)~ 2025年2月28日(金)の間に、庄内空港発着の航空便へご搭乗のうえ乗合タクシーをご利用いただくと、通常 大人(中学生以上)1,860円/人の料金が 1,000円/人になる割引キャンペーンを実施中!</div>
                          <div className={classes.redZone}>※ご利用には、事前の予約申し込みと、当日の搭乗券やeチケット控えなどのご提示が必要です。</div>
                          <div>※小学生以下は半額(500円/人)となります。</div>
                        </div>
                      }
                    </div>
                  }
                </Grid>
                {/* 送迎パターン選択の行。 */}
                <Grid item container>
                  <Grid item xs={4} className={classes.tableLeft}>
                    <Grid container direction="column" justify="center" style={{ height: "100%" }}>
                      <Grid item>
                        <Typography className={classes.textItemName}>
                          {t('taxi:select_pickup_pattern')}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={8} className={classes.contents}>
                    <FormControl component="fieldset">
                      <RadioGroup value={pickupPattern} aria-label="pick-up pattern" name="pick-up-pattern-radios">
                        {/** 【お迎え先 → 空港】の選択肢 */}
                        {(taxiCompanyConf?.supportDirection !== 'fromAirport') &&
                          <FormControlLabel
                            value={PickupPattern.TO_AIRPORT}
                            control={<Radio onClick={() => {setPickupPattern(PickupPattern.TO_AIRPORT)}} />}
                            label={t('taxi:point_to_airport')}
                          />
                        }
                        {/** 【空港 → お送り先】の選択肢 */}
                        {(taxiCompanyConf?.supportDirection !== 'toAirport') &&
                          <FormControlLabel
                            value={PickupPattern.FROM_AIRPORT}
                            control={<Radio onClick={() => {setPickupPattern(PickupPattern.FROM_AIRPORT)}} />}
                            label={t('taxi:airport_to_point')}
                          />
                        }
                      </RadioGroup>
                    </FormControl>
                    {/** 許可されていない送迎パターンを選択した際のアラート */}
                    {disallowSupportDirection &&
                      <Alert severity="error">
                        {t('taxi:invalid_ride_pattern')}
                      </Alert>
                    }
                  </Grid>
                </Grid>

                {/* お迎え先空港の行。 */}
                { !toAirport &&
                  <Grid item container>
                    <Grid item xs={4} className={classes.tableLeft}>
                      <Typography className={classes.textItemName}>
                        {t('taxi:pickup_airport')}
                      </Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <div className={classes.contents} data-cy="booking_taxi_select_airport" >
                        <SelectAirport
                          value={airportCode}
                          onChange={onChangedAirport}
                          targetAirportCodeList={targetAirportCodeList}
                          disabled={taxiCompany === TaxiCompanyList.dummyVal && mode === AirportTransferTaxiMapMode.select}
                        />
                        {t('taxi:airport')}
                      </div>
                    </Grid>
                  </Grid>
                }


                {/**
                  * 【デバッグ専用機能】検証用ダミー会社 dummyVal のみ表示。
                  * 地図モードとプルダウンモードを自由に切り替えるための開発・検証用ボタン。
                  * 本番の実タクシー会社では絶対に表示されない（dummyVal限定）。
                  */}
                { taxiCompany === TaxiCompanyList.dummyVal &&
                  <Grid item style={{ width: "100%" }}>
                    <div className={classes.debugModeToggle}>
                      <span className={classes.debugModeLabel}>🛠 DEBUG（検証用・dummyVal限定）</span>
                      <Button
                        variant="outlined"
                        size="small"
                        className={classes.debugModeToggleButton}
                        onClick={() => { void toggleDebugPulldownMode(); }}
                      >
                        {mode === AirportTransferTaxiMapMode.select ? '地図モードに切替' : 'プルダウンモードに切替'}
                      </Button>
                    </div>
                  </Grid>
                }
                { mode === AirportTransferTaxiMapMode.map &&
                  <>
                  <Grid item className={classes.tableLeft} style={{ width: "100%" }}>
                    <Typography className={classes.textItemName}>
                      <span>
                        { toAirport ? t('taxi:pickup_location') : t('taxi:sending_location') }
                      </span><br/>
                      <span className={classes.textItemAnnotation}>
                        { taxiCompany === TaxiCompanyList.sakataNo1Taxi
                          ? t('taxi:location_notice_sakataNo1Taxi').split('\n').map((s, idx) => (
                              <div key={idx}>{s}</div>
                            ))
                          : t('taxi:location_notice')
                        }
                      </span>
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Map
                      center={center}
                      markerPoint={markerPoint}
                      setCenter={setCenter}
                      setMarkerPoint={setMarkerPoint}
                      pointName={pointName}
                      setPointName={setPointName}
                      setPlaceJP={setPlaceJP}
                      setMunicipality={setMunicipality}
                      switchEdited={switchEdited}
                      setPlace={setPlace}
                    />
                  </Grid>
                  </>
                }
                {/** 乗降地点選択プルダウンメニュー */}
                {/** 多言語化対応はイースタンのみのため対応をしない（2022/02/18 現在） */}
                { (mode === AirportTransferTaxiMapMode.select && !isKisakataTaxi) &&
                  <Grid item container>
                    <Grid item xs={4} className={classes.tableLeft}>
                      <Typography className={classes.textItemName}>
                        <span>
                          { toAirport? 'お迎え場所': '降車場所'}
                        </span>
                        <br/>
                        <span>
                          ※プルダウンで定額対象施設を選択してください
                        </span>
                      </Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <div className={classes.contents} data-cy="booking_taxi_select_area">
                        <Select
                          value={selectPointNumber}
                          className={classes.seaGaiaSelect}
                          SelectDisplayProps={{
                            style: {
                              padding: '10px 20px 10px 5px',
                              whiteSpace: 'unset'
                            }
                          }}
                          onChange={(e: any) => {setSelectPointNumber(e.target.value);}}
                          fullWidth
                        >
                          { pointList.map((x, a) => {
                            return (
                              <MenuItem value={a} key={`seaGaiaMenuItem_${a}`} style={{fontSize:'0.8rem'}}>
                                {x.name}
                              </MenuItem>
                            )
                          }) }
                        </Select>
                      </div>
                    </Grid>
                  </Grid>
                }

                {/** 乗降地点選択プルダウンメニュー */}
                {/** 象潟合同交通 */}
                { (mode === AirportTransferTaxiMapMode.select && isKisakataTaxi) &&
                  <Grid item container>
                    <Grid item xs={4} className={classes.tableLeft}>
                      <Typography className={classes.textItemName}>
                        <span>
                          { toAirport? 'お迎え場所': '降車場所'}
                        </span>
                        <br/>
                        <span>
                          ※プルダウンで定額対象施設を選択してください
                        </span>
                      </Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <div className={classes.contents} data-cy="booking_taxi_select_area">
                        <Select
                          value={selectPointNumber}
                          className={classes.seaGaiaSelect}
                          SelectDisplayProps={{
                            style: {
                              padding: '10px 20px 10px 5px',
                              whiteSpace: 'unset'
                            }
                          }}
                          onChange={(e: any) => {
                            const value = Number(e.target.value);
                            setSelectPointNumber(e.target.value);
                            if (value === -1) {
                              // 未選択の場合
                              setPointName('ここで検索');
                              setCenter({ lat: 0, lng: 0 });
                            } else {
                              // 地点が選択された場合
                              setPointName(pointList[value].name);
                              setCenter({
                                lat: Number(pointList[value].latLng.split(',')[0]), 
                                lng: Number(pointList[value].latLng.split(',')[1]) 
                              });
                            }
                          }}
                          fullWidth
                        >
                          <MenuItem value={-1} key="unselected" style={{fontSize:'0.8rem'}}>
                            選択してください
                          </MenuItem>
                          { pointList.map((x, a) => {
                            return (
                              <MenuItem value={a} key={`seaGaiaMenuItem_${a}`} style={{fontSize:'0.8rem'}}>
                                {x.name}
                              </MenuItem>
                            )
                          }) }
                        </Select>
                        {taxiCompany === TaxiCompanyList.nikaho_go &&
                          <Typography color="error" style={{fontSize: '0.875rem', marginTop: '8px'}}>
                            ※ 平日/土日祝日で運行区間が異なります。<br/>
                            &nbsp;&nbsp;&nbsp;ご乗車日を変更した場合は、あらためて区間をご選択ください。
                          </Typography>
                        }
                      </div>
                    </Grid>
                  </Grid>
                }

                {/* お送り先空港の行。 */}
                { toAirport &&
                  <Grid item container>
                    <Grid item xs={4} className={classes.tableLeft}>
                      <Typography className={classes.textItemName}>
                        {t('taxi:sending_airport')}
                      </Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <div className={classes.contents} data-cy="booking_taxi_select_airport">
                        <SelectAirport
                          value={airportCode}
                          onChange={onChangedAirport}
                          targetAirportCodeList={targetAirportCodeList}
                          disabled={taxiCompany === TaxiCompanyList.dummyVal && mode === AirportTransferTaxiMapMode.select}
                        />
                        {t('taxi:airport')}
                      </div>
                    </Grid>
                  </Grid>
                }

                {/* 利用便の行。 */}
                <Grid item container>
                  <Grid item xs={4} className={classes.tableLeft}>
                    <Typography className={classes.textItemName}>
                      {/* 庄内エリア・それ以外で文言を変える */}
                      {isFlightConnectionSharedTaxi &&
                        <>
                          {t('taxi:flight_number_shonai_taxi')}
                          { toAirport ? t('taxi:dep_time') : t('taxi:arr_time') }
                        </>
                      }
                      {!isFlightConnectionSharedTaxi &&
                        <>
                          {t('taxi:flight_number')}
                          { toAirport ? t('taxi:dep_time') : t('taxi:arr_time') }
                        </>
                      }
                    </Typography>
                  </Grid>
                  <Grid item xs={8} container direction='column'>
                    <div className={classes.contents}>
                      {/* ご利用便の入力部分。 */}
                      <Grid item container>
                        <Grid item xs={12} md={5}>
                          <span className={classes.flightBefore}>
                            {t('taxi:flight_before')}
                          </span>
                          <TextField
                            value={flight}
                            onChange={onChangedFlight}
                            error={hasErrorInFlight}
                            helperText={getFlightHelperLabel()}
                          />
                          {t('taxi:flight_after')}
                        </Grid>
                        <Grid item xs={12} md={7}>
                          <Typography color='textSecondary' component='span' className={classes.supplement}>
                            {t('taxi:example_flight_number')}
                          </Typography>
                        </Grid>
                      </Grid>
                      {/* 乗車日時の選択部分。 */}
                      <Grid item>
                        <SelectYear value={flightDate} onChange={onChangedFlightDate.year}/>
                        {t('taxi:year')}
                        <SelectMonth value={flightDate} onChange={onChangedFlightDate.month}/>
                        {t('taxi:month')}
                        <SelectDay value={flightDate} onChange={onChangedFlightDate.day}/>
                        {t('taxi:day')}
                        <SelectFlightHour value={flightDate} onChange={onChangedFlightDate.hour} isUnselectedFlightHour={isUnselectedFlightHour}/>
                        {t('taxi:hour')}
                        <SelectFlightMinutes value={flightDate} onChange={onChangedFlightDate.minutes} isUnselectedFlightMinutes={isUnselectedFlightMinutes}/>
                        {t('taxi:minutes')}
                        {t('taxi:jst')}
                      </Grid>
                      {/* ご利用便日時に関するアラート */}
                      {isUnselectedFlightDate &&
                        <Alert severity='error' className={classes.alert}>
                          {invalidFlightDateErrorMessage}
                        </Alert>
                      }
                    </div>
                  </Grid>
                </Grid>

                {/* 乗車日時の行。 */}
                <Grid item container>
                  <Grid item xs={4} className={classes.tableLeft}>
                    <Typography className={classes.textItemName}>
                    {isFlightConnectionSharedTaxi && t('taxi:select_flight').split('\n').map((s, idx) => {
                      return (
                        <div key={idx}>
                          {s}
                        </div>
                      )
                    })}
                    {isKisakataTaxi && t('taxi:share_taxi').split('\n').map((s, idx) => {
                      return (
                        <div key={idx}>
                          {s}
                        </div>
                      )
                    })}
                    {(!isFlightConnectionSharedTaxi && !isKisakataTaxi) &&
                      <>
                        {t('taxi:boarding_date')}
                      </>
                    }
                    </Typography>
                  </Grid>
                  <Grid item xs={8} className={classes.contents}>
                    <div>
                      <span>
                        <SelectYear value={ridingDate} onChange={onChangedRidingYear}/>
                        {t('taxi:year')}
                        <SelectMonth value={ridingDate} onChange={onChangedRidingMonth}/>
                        {t('taxi:month')}
                        <SelectDay value={ridingDate} onChange={onChangedRidingDay}/>
                        {t('taxi:day')}
                      </span>

                      {isFlightConnectionSharedTaxi ? (
                        <span>
                          <SelectFlight
                            flightPattern={flightPattern}
                            onChange={onChangedRidingFlight}
                            selectFlightData={selectFlightData}
                          />
                          {t('taxi:flight_after')}
                          {t('taxi:connect_taxi')}
                        </span>
                      ):(
                        <>
                          {/** 任意に時刻を選べる場合 */}
                          {timePattern === TaxiTimePattern.normal &&
                            <span>
                              <SelectHour value={ridingDate} onChange={onChangedRidingHour}/>
                              {t('taxi:hour')}
                              <SelectMinutes value={ridingDate} onChange={onChangedRidingMinutes}/>
                              {t('taxi:minutes')}
                            </span>
                          }
                          {/** 選択肢より時刻を選べる場合 */}
                          {timePattern === TaxiTimePattern.pulldown &&
                            <SelectHourAndMinutesPulldown
                              value={ridingDate}
                              onChange={onChangedRidingHourAndMinutes}
                              selectTimeList={selectTimeList}
                              isUnselectedRidingDate={isUnselectedRidingDate}
                            />
                          }
                          <span>
                          {t(isKisakataTaxi ? 'taxi:dep_taxi' : 'taxi:jst')}
                          </span>
                        </>
                      )}
                    </div>
                    {/* 予約期限を過ぎている場合の警告。 */}
                    { isOverDeadline &&
                        <Alert severity='error' className={classes.alert}>
                          {invalidFlightErrorMessage}
                        </Alert>
                    }
                    {/* タクシー乗車に関する注意事項 */}
                    {(!isFlightConnectionSharedTaxi && !isKisakataTaxi) &&
                      <Typography color='error' className={classes.supplement}>
                        {t('taxi:congestion_notice_1')}
                        <br />
                        &ensp;{t('taxi:congestion_notice_2')}
                      </Typography>
                    }
                    {/* 象潟合同交通かつfrom空港の場合 */}
                    {(isKisakataTaxi && !toAirport) &&
                      <Typography color='error' className={classes.supplement}>
                        {t('taxi:kisakata_notice_1').split('\n').map((s, idx) => {
                          return (
                            <div key={idx}>
                              {s}
                            </div>
                          )
                        })}
                      </Typography>
                    }
                    {/* 象潟合同交通かつto空港の場合 */}
                    {(isKisakataTaxi && toAirport) &&
                      <Typography color='error' className={classes.supplement}>
                        {t('taxi:kisakata_notice_2')}
                      </Typography>
                    }
                    {/* 酒田第一タクシーかつto空港の場合 */}
                    {(toAirport && taxiCompany === TaxiCompanyList.sakataNo1Taxi) &&
                      <Typography color='error' className={classes.supplement}>
                        {t('taxi:congestion_notice_3')}
                        <br />
                        {t('taxi:congestion_notice_4')}
                      </Typography>
                    }
                    {/* 磐城タクシーかつto空港の場合 */}
                    {(toAirport && taxiCompany === TaxiCompanyList.iwakiTaxi) &&
                      <Typography color='error' className={classes.supplement}>
                        {t('taxi:congestion_notice_9')}
                        <br />
                        {t('taxi:congestion_notice_4')}
                      </Typography>
                    }
                    {/* 庄交ハイヤーかつto空港の場合 */}
                    {(toAirport && taxiCompany === TaxiCompanyList.shokoHire) &&
                      <Typography color='error' className={classes.supplement}>
                        {t('taxi:congestion_notice_3')}
                        <br />
                        {t('taxi:congestion_notice_5')}
                      </Typography>
                    }
                    {/* 酒田第一タクシーかつfrom空港の場合 */}
                    {(!toAirport && isSakataBaseformTaxi) &&
                      <Typography color='error' className={classes.supplement}>
                        {t('taxi:congestion_notice_6')}
                        <br />
                        {t('taxi:congestion_notice_7')}
                      </Typography>
                    }
                    {/* 庄交ハイヤーかつfrom空港の場合 */}
                    {(!toAirport && taxiCompany === TaxiCompanyList.shokoHire) &&
                      <Typography color='error' className={classes.supplement}>
                        {t('taxi:congestion_notice_6')}
                        <br />
                        {t('taxi:congestion_notice_8')}
                        <br />
                        {t('taxi:congestion_notice_7')}
                      </Typography>
                    }
                    {/** 対応便が選択されていないときのアラート */}
                    {invalidFlightPattern &&
                      <Alert severity="error">
                        乗合タクシーの接続便を選択してください
                      </Alert>
                    }
                  </Grid>
                </Grid>

                {/* メールアドレスの行。 */}
                <Grid item container>
                  <Grid item xs={4} className={classes.tableLeft}>
                    <Typography className={classes.textItemName}>
                      {t('taxi:mail_address')}
                    </Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <div className={classes.contents}>
                      <TextField
                        variant='outlined'
                        placeholder={t('taxi:email_example')}
                        value={email}
                        onChange={onChangedEmail}
                        error={hasErrorInEmail}
                        helperText={getEmailHelperLabel()}
                        fullWidth
                        inputProps={{
                          style: {
                            padding: '3px',
                            fontSize: '1rem'
                          }
                        }}
                      />
                    </div>
                  </Grid>
                </Grid>
                {/* dev/stg環境のみ表示するチェックボックス。 */}
                { (Environment.isDev(env) || Environment.isStaging(env)) &&
                  <Grid item container>
                    <Grid item xs={4} className={classes.tableLeft}>
                      <Typography className={classes.textItemName}>
                        reCAPTCHA v2で認証する<br/>
                        ※開発・ステージング環境のみ表示
                      </Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <div className={classes.contents}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={useRecaptchaV2}
                              onChange={(event) => dispatch(switchUseV2(event.target.checked))}
                            />
                          }
                          label="reCAPTCHA v2で認証する"
                        />
                      </div>
                    </Grid>
                  </Grid>
                }
                {/* dev/stg環境のみ表示する、予約操作時刻(reservationDatetime)の検証用上書きフォーム。 */}
                { (Environment.isDev(env) || Environment.isStaging(env)) &&
                  <Grid item container>
                    <Grid item xs={4} className={classes.tableLeft}>
                      <Typography className={classes.textItemName}>
                        予約操作時刻(reservationDatetime)を上書き<br/>
                        ※開発・ステージング環境のみ表示<br/>
                        ※料金の新旧切替(periodicTariffByReservationTime)検証用。空欄なら実際の現在時刻を使用
                      </Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <div className={classes.contents}>
                        <TextField
                          value={debugReservationDatetime}
                          onChange={(event) => setDebugReservationDatetime(event.target.value)}
                          placeholder="yyyyMMddHHmm 例: 202610020900"
                          fullWidth
                        />
                      </div>
                    </Grid>
                  </Grid>
                }
                <Grid item>
                  {/* 対象エリアの場合は所要時間・距離・料金を表示する。 */}
                  { (isShowTaxiFeeArea() && isSubjectArea) &&
                    <Cost label={
                      <span data-cy="booking_taxi_cost_area">
                        { costLabel() }
                        {/* モバイルサイズ（画面幅960px未満）の場合は改行を入れる。 */}
                        { isMobileSize && <br /> }
                        {/* デスクトップサイズ（画面幅960px以上）の場合は空白を入れる。 */}
                        { !isMobileSize && <>&nbsp;</> }
                        { feeLabel }
                        {/** 注意書きがある場合には全て表示する */}
                        { (notesLabel && notesLabel.length > 0) &&
                          <span data-cy="booking_taxi_notes_area">
                            {notesLabel.map((label, index)=>{
                              return (
                                <span key={index}>
                                  <br/>
                                  <span>{label}</span>
                                </span>
                              )
                            })}
                          </span>
                        }
                      </span>
                      }
                    />
                  }
                  {/* 対象エリア外の場合は固定のメッセージを表示する。 */}
                  { (isShowTaxiFeeArea() && !isSubjectArea) &&
                    <Cost label={t('taxi:notice_outside_target_area')} />
                  }
                  {/* タクシー料金APIに金額を問い合わせ中の時 */}
                  { !isEndCallTaxiAPI &&
                    <Cost label={
                      <CircularProgress color='primary' size={48}/>
                    }/>
                  }
                  {/* タクシー料金APIから結果が得られなかった時 */}
                  { isFailCallTaxiAPI &&
                    <Cost label={t('taxi:notice_fail_get_fee')} />
                  }
                </Grid>
                {/* 警告を表示する行。 */}
                <Grid item>
                  {/* 予約に失敗した場合はその旨の警告を表示する。 */}
                  { isFailed && <AlertInReservation type='fail'/> }
                  { isRequiredRetry && <AlertInReservation type='retry'/> }
                  { isUnavalableEmail && <AlertInReservation type='email'/> }
                  {/* reCAPTCHA v2でのリトライを促す場合の警告。 */}
                  { (isSubmitted && needRetryRecaptcha) && <AlertInReservation type='retry_with_v2' /> }
                  { (hasErrorInEmail || hasErrorInFlight) && <AlertInReservation type='input'/> }
                  { (isEmptyAddress && errorMessage === '' && !isKisakataTaxi) &&
                    <AlertInReservation
                      type='address'
                      pickupPattern={pickupPattern}
                    />
                  }
                  { (isEmptyAddress && errorMessage === '' && isKisakataTaxi) &&
                    <AlertInReservation
                      type='unselected'
                      pickupPattern={pickupPattern}
                    />
                  }
                  {/** 料金取得APIから任意のアラートResponseが来たときには掲示する */}
                  { alertLabel.length > 0 &&
                    <AlertInReservation
                      anyMessage={alertLabel}
                    />
                  }
                  {/** errorMessageに格納された任意の警告を表示する */}
                  { errorMessage !== '' &&
                    <AlertInReservation
                      anyMessage={
                        [
                          {
                            type: 'alert',
                            message: errorMessage
                          }
                        ]
                      }
                    />
                  }
                </Grid>
                {/* 予約するボタンの行。 */}
                <Grid item>
                  <ButtonToSubmit onClick={handleClick} disabled={!isAvailableReservation} />
                </Grid>
                {/* 注意事項。 */}
                <Grid item>
                  <Cautions
                    isMeterFare={fareType === 'PAYG'}
                    companyDisplayName={taxiCompanyConf?.temporaryDisplayName || ''}
                    isSupportAMC={taxiCompanyConf?.supportAMC.includes(airportCode)}
                    company={taxiCompany}
                  />
                </Grid>
                {/* お問い合わせ先。 */}
                <Grid item>
                  <Contact
                    company={taxiCompany}
                  />
                </Grid>
              </Grid>
            </Container>
          }
          <AirportTransferTaxiFooter tabiCube={tabiCube} />
        </>
    </div>
  );
}

export default Booking;
